import {t} from "@lingui/core/macro";
import type {CSSProperties} from "aphrodite";
import memoize from "lodash.memoize";
import * as React from "react";

import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";

import type {ActivityKind} from "./types";

interface Props {
    selectedActivityFilter: ActivityKind | null | undefined;
    handleActivityFilterChange: (
        value?: ActivityKind | null | undefined,
    ) => void;
    style?: CSSProperties;
}

const getActivityOptions = memoize(
    (): Array<{
        value: ActivityKind | "All";
        text: string;
    }> => [
        {
            value: "Exercise",
            text: t`Exercises`,
        },
        {
            value: "Video",
            text: t`Videos`,
        },
        {
            value: "Article",
            text: t`Articles`,
        },
        {
            value: "TopicQuiz",
            text: t`Quizzes`,
        },
        {
            value: "TopicUnitTest",
            text: t`Unit tests`,
        },
        {
            value: "CourseChallenge",
            text: t`Course challenges`,
        },
        {
            value: "MasteryChallenge",
            text: t`Mastery challenges`,
        },
        {
            value: "AIGuideActivities",
            text: t`Khanmigo activities`,
        },
        {
            value: "AiGuideWriting",
            text: t`Writing Coach`,
        },
        {
            value: "All",
            text: t`All activities`,
        },
    ],
);

const getActivityFilter = (value: string): ActivityKind | null | undefined => {
    const activityFilter = getActivityOptions().find((v) => v.value === value);
    return !activityFilter || activityFilter.value === "All"
        ? null
        : activityFilter.value;
};

const ActivitySessionsActivitySelect = (props: Props): React.ReactElement => {
    const {selectedActivityFilter, handleActivityFilterChange, style} = props;
    return (
        <SingleSelect
            aria-label={t`Activity type`}
            selectedValue={
                selectedActivityFilter ? selectedActivityFilter : "All"
            }
            onChange={(value: string) => {
                const activityFilter = getActivityFilter(value);
                handleActivityFilterChange(activityFilter);
            }}
            style={style}
            placeholder=""
            testId="activity-type-filter"
        >
            {getActivityOptions().map((option) => (
                <OptionItem
                    key={option.value}
                    value={option.value}
                    label={option.text}
                />
            ))}
        </SingleSelect>
    );
};

export default ActivitySessionsActivitySelect;
